// src/features/faq/components/ClientsListFAQs.tsx

import React from 'react';
// ELIMINAMOS: import { useNavigate } from 'react-router-dom';
import { Users, Crown, ArrowRight } from 'lucide-react'; // Añadimos Crown y ArrowRight

export interface ClientsListFAQItem {
  id: string;
  question: string;
  answer: React.ReactNode;
}

interface ClientsListFAQData {
  id: string;
  title: string;
  icon: React.ReactNode;
  description: string;
  faqs: ClientsListFAQItem[];
}

// AÑADIMOS el parámetro 'navigateCallback'
export const ClientsListFAQs = (navigateCallback: (path: string) => void): ClientsListFAQData => {
  // ELIMINAMOS: const navigate = useNavigate();

  return {
    id: 'clients',
    title: 'Lista de Clientes',
    icon: <Users className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />,
    description: 'Consulta y gestión de la base de datos completa de tus clientes registrados.',
    faqs: [
      {
        id: 'clients-search',
        question: '¿Cómo busco un cliente específico en la lista completa?',
        answer: (
          <div>
            <p>Para buscar un cliente dentro de tu base de datos general:</p>
            <ol className="list-decimal ml-5 mt-2 space-y-1">
              <li>Accede a la <button onClick={() => navigateCallback('/dashboard/clients')} className="text-accent hover:text-blue-800 dark:text-blue-300 font-medium">Lista de Clientes</button> desde el menú lateral.</li>
              <li>Utiliza la barra de búsqueda que se encuentra en la parte superior de la página.</li>
              <li>Puedes ingresar el nombre del cliente o su número telefónico para filtrar la lista.</li>
              <li>Los resultados se irán actualizando dinámicamente a medida que escribes.</li>
            </ol>
            <div className="mt-2 bg-amber-50 dark:bg-amber-500/10 p-3 rounded-md border border-amber-200 dark:border-amber-500/30">
              <p className="text-sm text-amber-700">
                <strong className="flex items-center"><Crown className="h-4 w-4 mr-1.5 text-yellow-600 dark:text-yellow-400" />Consejo PRO:</strong> La búsqueda general ya incluye nombre, teléfono y correo. La "búsqueda avanzada", con filtros adicionales como rango de puntos o segmentos, es una característica de nuestros planes PRO.
                <button onClick={() => navigateCallback('/dashboard/subscription')} className="ml-1 text-pink-600 dark:text-pink-400 hover:text-pink-800 dark:text-pink-300 font-medium inline-flex items-center">
                  Actualiza tu plan <ArrowRight className="h-3 w-3 ml-1" />
                </button> para explorar opciones de búsqueda más potentes.
              </p>
            </div>
          </div>
        )
      },
      {
        id: 'clients-history',
        question: '¿Cómo puedo ver el historial detallado de un cliente?',
        answer: (
          <div>
            <p>Para acceder al historial completo de visitas, puntos y otras interacciones de un cliente:</p>
            <ol className="list-decimal ml-5 mt-2 space-y-1">
              <li>Navega a la <button onClick={() => navigateCallback('/dashboard/clients')} className="text-accent hover:text-blue-800 dark:text-blue-300 font-medium">Lista de Clientes</button>.</li>
              <li>Encuentra al cliente deseado en la lista (puedes usar la barra de búsqueda).</li>
              <li>Haz clic en la <strong>fila del cliente</strong>.</li>
              <li>Esto te redirigirá a su página de perfil/historial individual, donde podrás ver:</li>
              <ul className="list-disc ml-8 mt-1">
                <li>Su información personal completa (teléfono, email, cumpleaños si los proporcionó).</li>
                <li>Un historial cronológico de todas sus visitas.</li>
                <li>Los puntos ganados y las recompensas canjeadas.</li>
                <li>Cualquier feedback o comentario que haya dejado (si aplica).</li>
                <li>Detalles de registro y última actualización.</li>
              </ul>
            </ol>
            <p className="mt-2 text-content-2">Este historial es muy valioso para entender los patrones de comportamiento de tus clientes y ofrecer un servicio más personalizado.</p>
          </div>
        )
      },
      {
        id: 'clients-points',
        question: '¿Es posible modificar los puntos de un cliente manualmente?',
        answer: (
          <div>
            <p>Sí, como administrador, tienes la capacidad de ajustar manualmente el saldo de puntos de cualquier cliente:</p>
            <ol className="list-decimal ml-5 mt-2 space-y-1">
              <li>Ve a la sección <button onClick={() => navigateCallback('/dashboard/recharge-points')} className="text-accent hover:text-blue-800 dark:text-blue-300 font-medium">Gestionar Puntos</button> en el menú lateral.</li>
              <li>Busca y selecciona al cliente al que deseas modificarle los puntos.</li>
              <li>Ingresa la cantidad de puntos que quieres agregar o restar.</li>
              <li>Elige la operación (sumar o quitar puntos).</li>
              <li>Confirma la acción. Los puntos del cliente se actualizarán inmediatamente.</li>
            </ol>
            <div className="mt-2 bg-amber-50 dark:bg-amber-500/10 p-3 rounded-md border border-amber-200 dark:border-amber-500/30">
              <p className="text-sm text-amber-700">
                <strong className="flex items-center"><Crown className="h-4 w-4 mr-1.5 text-yellow-600 dark:text-yellow-400" />Función PRO:</strong> La capacidad de gestionar puntos manualmente (agregar o restar) está disponible exclusivamente para usuarios con suscripción PRO. 
                <button onClick={() => navigateCallback('/dashboard/subscription')} className="ml-1 text-pink-600 dark:text-pink-400 hover:text-pink-800 dark:text-pink-300 font-medium inline-flex items-center">
                  Actualiza tu plan <ArrowRight className="h-3 w-3 ml-1" />
                </button> para esta opción.
              </p>
            </div>
            <p className="mt-2 text-content-2">Esta función es útil para corregir errores, otorgar bonificaciones especiales, compensar por alguna situación particular, o como parte de promociones específicas.</p>
          </div>
        )
      },
      {
        id: 'clients-export',
        question: '¿Cómo puedo exportar mi lista completa de clientes?',
        answer: (
          <div>
            <p>Puedes exportar tu base de datos de clientes a un archivo Excel (.xlsx) para análisis externos o para tus propios registros:</p>
            <ol className="list-decimal ml-5 mt-2 space-y-1">
              <li>Accede a la <button onClick={() => navigateCallback('/dashboard/clients')} className="text-accent hover:text-blue-800 dark:text-blue-300 font-medium">Lista de Clientes</button>.</li>
              <li>En la parte superior de la página, encontrarás el botón <strong>"Descargar Reporte Completo"</strong>. Haz clic en él.</li>
              <li>Se abrirá una ventana donde puedes <strong>elegir qué columnas incluir</strong>. Al confirmar, se descarga un Excel con <strong>todos los clientes que coincidan con el filtro actual</strong> (hasta 5,000), no solo la página visible.</li>
            </ol>
            <div className="mt-2 bg-amber-50 dark:bg-amber-500/10 p-3 rounded-md border border-amber-200 dark:border-amber-500/30">
              <p className="text-sm text-amber-700">
                <strong className="flex items-center"><Crown className="h-4 w-4 mr-1.5 text-yellow-600 dark:text-yellow-400" />Función PRO:</strong> La exportación de la lista de clientes a Excel es una funcionalidad disponible exclusivamente para usuarios con suscripción PRO. 
                <button onClick={() => navigateCallback('/dashboard/subscription')} className="ml-1 text-pink-600 dark:text-pink-400 hover:text-pink-800 dark:text-pink-300 font-medium inline-flex items-center">
                  Actualiza tu plan <ArrowRight className="h-3 w-3 ml-1" />
                </button> para acceder a esta herramienta.
              </p>
            </div>
            <p className="mt-2 text-content-2">El archivo exportado puede incluir: nombre, teléfono, país, email, cumpleaños, fecha de última visita, puntos y fecha de registro (según los datos disponibles de cada cliente).</p>
          </div>
        )
      },
    ]
  };
};

// export default ClientsListFAQs; // Opcional